import { RefObject, useEffect, useRef } from 'react';

/**
 * Тип для элементов, которые нужно игнорировать при клике вне основного элемента.
 * Может быть либо рефом на DOM-элемент, либо CSS-селектором.
 */
type IgnoreElement = RefObject<HTMLElement> | string;

/**
 * Хук для отслеживания кликов вне заданного элемента с учетом игнорируемых элементов.
 * Вызывает переданный обработчик, если клик произошел за пределами основного элемента
 * и не попал в игнорируемые элементы (рефы или селекторы).
 *
 * @template T - Тип DOM-элемента, на который ссылается основной ref.
 * @param {React.RefObject<T>} ref - Реф на основной элемент, клики внутри которого игнорируются.
 * @param {(event: MouseEvent | TouchEvent) => void} handler - Функция, вызываемая при клике вне элемента.
 * @param {Array<IgnoreElement>} [ignoreElements=[]] - Массив элементов для игнорирования (рефы или CSS-селекторы).
 * @returns {void}
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * const extraRef = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => console.log('Clicked outside'), [extraRef, '#toast', '.react-datepicker']);
 *
 * @remarks
 * Обратите внимание, что `ref`, `handler` и `ignoreElements` добавлены в зависимости эффекта.
 * Поскольку переданный `handler` может быть новой функцией при каждом рендере, это вызовет
 * повторный запуск эффекта и очистки при каждом рендере. Это не критично, но для оптимизации
 * рекомендуется обернуть `handler` в `useCallback` перед передачей в хук.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  handler: (event: MouseEvent | TouchEvent) => void,
  ignoreElements: Array<IgnoreElement> = []
): void {
  const wasMouseDownInside = useRef<boolean>(false);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      wasMouseDownInside.current = !!ref.current?.contains(
        event.target as Node
      );
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (
        !ref.current ||
        wasMouseDownInside.current ||
        ref.current.contains(target) ||
        (target as Element)?.closest('.Toastify__toast') ||
        (target as Element)?.closest('.react-datepicker')
      ) {
        return;
      }

      const shouldIgnore = ignoreElements.some((element) => {
        if (typeof element === 'string') {
          return (target as Element)?.closest(element);
        }

        return element?.current?.contains(target);
      });

      if (shouldIgnore) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler, ignoreElements]);
}
